// 封装游戏分类页的组件以及数据获取
import { getDeviceType } from "@/utils/device";
import { cleanData, ensureAbsoluteImageUrl, formatGameData } from "@/utils/methods";
import { useState, useEffect, useMemo } from "react";
import { useRouter } from 'next/router';
import GameGrid from 'components/GameGrid';
import dynamic from 'next/dynamic';

const LIMIT = 48;

// 游戏分类页面
export function MenuPageDetail({initialGames, initialDeviceType, initialTotal, categorySlug, currentPage: initialPage}) {
    const router = useRouter();
    const [currentPage, setCurrentPage] = useState(initialPage || 1);
    const [gameList, setGameList] = useState(initialGames);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(initialTotal);
    
    const limit = LIMIT;
    const totalPages = Math.ceil(total / limit);

    // 分离路由参数变化的处理，避免依赖循环
    useEffect(() => {
        const newPage = initialPage || 1;
        setCurrentPage(newPage);
        setGameList(initialGames);
        setTotal(initialTotal);
    }, [categorySlug, initialPage, initialGames, initialTotal]); // 移除currentPage依赖

    // 处理分页变化
    const handlePageChange = async (page) => {
        // 通过路由导航到不同的页面
        if (!categorySlug) {
            console.error('categorySlug is required for pagination');
            return;
        }
        
        if (page === 1) {
            // 第一页直接用 /game/music 形式
            router.push(`/game/${categorySlug}`);
        } else {
            // 其他页面用 /game/music/2 形式
            router.push(`/game/${categorySlug}/${page}`);
        }
    };

    return <GameGrid
        gameList={gameList}
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        categorySlug={categorySlug}
    />;
}

// 游戏分类页面的服务端数据获取
export const getMenuServerSideProps = async context => {
    // 在服务端导入必要的类型和客户端
    const { grpcClientManager } = await import('@/lib/grpc/client-manager');
    const { SupportedLanguage } = await import('@/generated/localization');
    
    const userAgent = context.req.headers['user-agent'] || '';
    const deviceType = getDeviceType(userAgent);
    const limit = LIMIT;
    const { slug, page } = context.query;
    const currentPage = parseInt(page) || 1;

    // 获取 gRPC 数据
    let grpcData = {
        games: [],
        total: 0
    };

    try {
        const gameClient = grpcClientManager.getGameClient();

        if (slug) {
            // 使用分类API获取游戏
            const categoryRequest = {
                id: 0,
                slug: slug,
                page: currentPage,
                pageSize: limit,
                language: SupportedLanguage.LANG_EN_US,
            };

            const categoryResponse = await gameClient.getGamesByCategory(categoryRequest);
            
            grpcData = {
                games: formatGameData(categoryResponse.games) || [],
                total: categoryResponse.total || 0
            };
        } else {
            // 使用游戏列表API获取所有游戏
            const gamesRequest = {
                page: currentPage,
                pageSize: limit,
                search: '',
                categoryIds: [],
                tagIds: [],
                status: '1',
                sortBy: 'created_at',
                sortOrder: 'desc'
            };

            const gamesResponse = await gameClient.getGames(gamesRequest);
            
            grpcData = {
                games: formatGameData(gamesResponse.games) || [],
                total: gamesResponse.total || 0
            };
        }

    } catch (error) {
        console.error('gRPC API 调用失败:', error);
        console.error('错误详情:', {
            message: error.message,
            stack: error.stack,
            code: error.code,
            details: error.details
        });
    }

    return {
        props: cleanData({
            page_name: '/',
            initialDeviceType: deviceType,
            initialGames: grpcData.games,
            initialTotal: grpcData.total,
            categorySlug: slug || null,
            currentPage: currentPage
        })
    };
};